import { get } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import { useVoiceSTT, useVoiceTTS } from '@/hooks/useVoiceProviders';
import { SPEECH_PROVIDER_NAMES } from '@/modules/developerTools/pages/speechModels/helper';
import { SpeechKind, SpeechStrategyStep } from '@/shared/types/speechStrategy';
import { TTSProvider } from '@/shared/types/voiceProviders';

import { MODEL_FIELD } from './helpers';

interface RegionDefaultSummaryProps {
  kind: SpeechKind;
  step: SpeechStrategyStep;
}

/** What the region default gives the row, read-only: provider, model and, for TTS, the voice. */
export const RegionDefaultSummary = ({ kind, step }: RegionDefaultSummaryProps) => {
  const { t } = useTranslation();
  const { getModelById: getSttModel } = useVoiceSTT();
  const { getModelById: getTtsModel, getVoicesByProvider } = useVoiceTTS();

  const modelField = MODEL_FIELD[kind][step.provider];
  const modelId = modelField ? (get(step.config, modelField) as string | undefined) : undefined;
  const model = modelId && (kind === 'stt' ? getSttModel(modelId) : getTtsModel(modelId));
  const voiceId = kind === 'tts' ? (step.config.voice_id as string | undefined) : undefined;
  const voice =
    voiceId &&
    getVoicesByProvider(step.provider as TTSProvider).find((entry) => entry.id === voiceId);

  const rows = [
    { label: t('ai_agents.speech_strategy.source'), value: SPEECH_PROVIDER_NAMES[step.provider] },
    { label: t('integrations.voice_common.voice.model'), value: model ? model.name : modelId },
    { label: t('integrations.voice_common.voice.voice_id'), value: voice ? voice.name : voiceId },
  ].filter((row) => row.value);

  return (
    <dl
      className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-md border border-border bg-muted/50 p-3 text-sm leading-5"
      data-testid={`region-default-${kind}`}
    >
      {rows.map((row) => (
        <div key={row.label} className="contents">
          <dt className="text-muted-foreground">{row.label}</dt>
          <dd className="min-w-0 truncate text-foreground">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
};
